/* Analýza V2+ — PĚT tabů závazků místo čtyř (P0-2).
 *
 * PROČ (nález UAT):
 * Původní taby byly bydleni | ostatni | pojistky | smlouvy. Pod „Bydlením"
 * seděly spotřebitelské úvěry, pod „Ostatní" podnikatelské. To je doménový
 * nesmysl: hypotéka, kreditka a provozní úvěr firmy se řeší jinak, jiným
 * produktem a jiným člověkem.
 *
 * Nové rozdělení:
 *   1) hypoteky   — Hypotéky a bydlení
 *   2) osobni     — Osobní úvěry
 *   3) firemni    — Firemní úvěry
 *   4) pojistne   — Pojistné smlouvy
 *   5) investicni — Investiční a spořicí smlouvy
 *
 * ⚠️ MIGRACE — NIC NESMÍ ZMIZET.
 * Klíč tabu je zároveň datovým klíčem. Uložené analýzy mají závazky ve dvou
 * polích: `zavazkyB` (dřív „bydlení") a `zavazkyO` (dřív „ostatní"). Datový
 * tvar se NEMĚNÍ — přidává se jen nepovinné pole `druh`, které říká, kam
 * řádek patří. Když `druh` chybí (= každá existující analýza), rozhodne
 * pole, ve kterém řádek leží:
 *     zavazkyB → hypoteky,  zavazkyO → osobni
 * Žádný řádek tedy nemůže vypadnout: rozdelZavazky() je totální rozklad
 * (hlídá to test „nic nezmizí").
 *
 * Čisté funkce, žádný React — testovatelné bez jsdom.
 */

export const HYPOTEKY = "hypoteky";
export const OSOBNI = "osobni";
export const FIREMNI = "firemni";
export const POJISTNE = "pojistne";
export const INVESTICNI = "investicni";

/** Pořadí tabů v UI. */
export const ZAVAZEK_TABY = [HYPOTEKY, OSOBNI, FIREMNI, POJISTNE, INVESTICNI];

/** Taby, jejichž obsahem jsou úvěrové řádky (zavazkyB / zavazkyO). */
export const UVEROVE_TABY = [HYPOTEKY, OSOBNI, FIREMNI];

/* Klíč tabu → klíč modulu souhrny.js (TABY = bydleni|ostatni|pojistky|investicni).
 * Firemní úvěry sdílejí s osobními datový tvar i výpočet, liší se výběrem
 * řádků — proto stejný modul, ale JINÁ data (viz dataProTab). */
export const TAB_MODUL = {
  [HYPOTEKY]: "bydleni",
  [OSOBNI]: "ostatni",
  [FIREMNI]: "ostatni",
  [POJISTNE]: "pojistky",
  [INVESTICNI]: "investicni",
};

/** Popisky tabů. */
export const TAB_LABEL = {
  cs: {
    [HYPOTEKY]: "Hypotéky a bydlení",
    [OSOBNI]: "Osobní úvěry",
    [FIREMNI]: "Firemní úvěry",
    [POJISTNE]: "Pojistné smlouvy",
    [INVESTICNI]: "Investiční a spořicí",
  },
  sk: {
    [HYPOTEKY]: "Hypotéky a bývanie",
    [OSOBNI]: "Osobné úvery",
    [FIREMNI]: "Firemné úvery",
    [POJISTNE]: "Poistné zmluvy",
    [INVESTICNI]: "Investičné a sporiace",
  },
  en: {
    [HYPOTEKY]: "Mortgages and housing",
    [OSOBNI]: "Personal loans",
    [FIREMNI]: "Business loans",
    [POJISTNE]: "Insurance contracts",
    [INVESTICNI]: "Investment and savings",
  },
  uk: {
    [HYPOTEKY]: "Іпотеки та житло",
    [OSOBNI]: "Особисті кредити",
    [FIREMNI]: "Бізнес-кредити",
    [POJISTNE]: "Страхові договори",
    [INVESTICNI]: "Інвестиції та заощадження",
  },
  vi: {
    [HYPOTEKY]: "Vay mua nhà và nhà ở",
    [OSOBNI]: "Vay cá nhân",
    [FIREMNI]: "Vay doanh nghiệp",
    [POJISTNE]: "Hợp đồng bảo hiểm",
    [INVESTICNI]: "Đầu tư và tiết kiệm",
  },
};

export function tabLabel(tab, lang = "cs") {
  const t = TAB_LABEL[lang] || TAB_LABEL.cs;
  return t[String(tab ?? "").trim()] || "";
}

/* ===================================================================
   DRUHY SMLUV — to, co poradce vybírá v kartě
=================================================================== */

/* Druh smlouvy v pěti jazycích.
 * NÁLEZ 9. 9. 2026: helper znal jen `cs` a `sk`, takže vietnamský poradce
 * vybíral druh úvěru z českého seznamu („Havarijní pojištění"). Chybějící
 * jazyk padá na češtinu — neúplný řádek proto nic nerozbije. */
const D = (k, cs, sk, en, uk, vi) => ({ k, cs, sk, en, uk, vi });
/** Popisek druhu v jazyce UI; neznámý jazyk i chybějící překlad → čeština. */
const POPIS_DRUH_UVERU = { cs: "Druh úvěru", sk: "Druh úveru", en: "Loan type", uk: "Тип кредиту", vi: "Loại khoản vay" };
const POPIS_DRUH_POJISTENI = { cs: "Druh pojištění", sk: "Druh poistenia", en: "Insurance type", uk: "Тип страхування", vi: "Loại bảo hiểm" };
export const druhPopisek = (x, lang = "cs") => (x && x[lang]) || (x && x.cs) || "";

export const DRUHY = {
  [HYPOTEKY]: [
    D("hypoteka", "Hypotéka", "Hypotéka", "Mortgage", "Іпотека", "Vay mua nhà"),
    D("uver_stavebko", "Úvěr ze stavebního spoření", "Úver zo stavebného sporenia", "Building savings loan", "Кредит із будівельних заощаджень", "Vay từ tiết kiệm nhà ở"),
    D("americka_hypoteka", "Americká hypotéka na nemovitost", "Americká hypotéka na nehnuteľnosť", "Home equity loan", "Кредит під заставу нерухомості", "Vay thế chấp bất động sản"),
    D("jiny_uver_bydleni", "Jiný úvěr spojený s bydlením", "Iný úver spojený s bývaním", "Other housing-related loan", "Інший кредит, пов'язаний із житлом", "Khoản vay khác liên quan nhà ở"),
  ],
  [OSOBNI]: [
    D("spotrebitelsky", "Spotřebitelský úvěr", "Spotrebiteľský úver", "Consumer loan", "Споживчий кредит", "Vay tiêu dùng"),
    D("kontokorent", "Kontokorent", "Kontokorent", "Overdraft", "Овердрафт", "Thấu chi"),
    D("kreditni_karta", "Kreditní karta", "Kreditná karta", "Credit card", "Кредитна картка", "Thẻ tín dụng"),
    D("revolving", "Revolving", "Revolving", "Revolving loan", "Револьверний кредит", "Vay tuần hoàn"),
    D("leasing_osobni", "Osobní leasing", "Osobný lízing", "Personal leasing", "Особистий лізинг", "Thuê tài chính cá nhân"),
  ],
  [FIREMNI]: [
    D("podnikatelsky", "Podnikatelský úvěr", "Podnikateľský úver", "Business loan", "Бізнес-кредит", "Vay kinh doanh"),
    D("provozni", "Provozní úvěr", "Prevádzkový úver", "Working capital loan", "Кредит на поточну діяльність", "Vay vốn lưu động"),
    D("firemni_revolving", "Firemní revolving", "Firemný revolving", "Corporate revolving loan", "Корпоративний револьверний кредит", "Vay tuần hoàn doanh nghiệp"),
    D("firemni_kontokorent", "Firemní kontokorent", "Firemný kontokorent", "Corporate overdraft", "Корпоративний овердрафт", "Thấu chi doanh nghiệp"),
    D("firemni_leasing", "Firemní leasing", "Firemný lízing", "Corporate leasing", "Корпоративний лізинг", "Thuê tài chính doanh nghiệp"),
  ],
  [POJISTNE]: [
    D("zivot", "Životní pojištění", "Životné poistenie", "Life insurance", "Страхування життя", "Bảo hiểm nhân thọ"),
    /* ÚRAZ CHYBĚL, PŘESTOŽE HO VÝCHOZÍ ANALÝZA MÁ JAKO ŘÁDEK.
       Model zakládá „Úrazové pojištění", ale v číselníku pro něj nebyla
       položka — poradce tedy u řádku viděl prázdné „—" a druh nešlo vybrat. */
    D("uraz", "Úrazové pojištění", "Úrazové poistenie", "Accident insurance", "Страхування від нещасних випадків", "Bảo hiểm tai nạn"),
    D("nemovitost", "Pojištění nemovitosti", "Poistenie nehnuteľnosti", "Property insurance", "Страхування нерухомості", "Bảo hiểm bất động sản"),
    D("domacnost", "Pojištění domácnosti", "Poistenie domácnosti", "Household contents insurance", "Страхування домашнього майна", "Bảo hiểm tài sản trong nhà"),
    D("odpovednost", "Pojištění odpovědnosti", "Poistenie zodpovednosti", "Liability insurance", "Страхування відповідальності", "Bảo hiểm trách nhiệm"),
    D("pravni_ochrana", "Pojištění právní ochrany", "Poistenie právnej ochrany", "Legal expenses insurance", "Страхування правового захисту", "Bảo hiểm chi phí pháp lý"),
    D("cestovni", "Cestovní pojištění", "Cestovné poistenie", "Travel insurance", "Туристичне страхування", "Bảo hiểm du lịch"),
    D("povinne_ruceni", "Povinné ručení", "Povinné zmluvné poistenie", "Motor third-party liability", "ОСЦПВ (авто)", "Bảo hiểm trách nhiệm xe cơ giới"),
    D("havarijni", "Havarijní pojištění", "Havarijné poistenie", "Motor own-damage insurance", "КАСКО", "Bảo hiểm vật chất xe"),
    D("podnikatelske", "Podnikatelské pojištění", "Podnikateľské poistenie", "Business insurance", "Страхування бізнесу", "Bảo hiểm kinh doanh"),
    D("zamestnavatelske", "Zaměstnavatelské pojištění", "Zamestnávateľské poistenie", "Employer's liability insurance", "Страхування відповідальності перед роботодавцем", "Bảo hiểm trách nhiệm với người sử dụng lao động"),
  ],
};

/** Druhy pro tab (prázdné pole = tab nemá výběr druhu). */
export function druhyProTab(tab) {
  return DRUHY[String(tab ?? "").trim()] || [];
}

export function druhLabel(druh, lang = "cs") {
  const k = String(druh ?? "").trim();
  for (const seznam of Object.values(DRUHY)) {
    const found = seznam.find((x) => x.k === k);
    if (found) return druhPopisek(found, lang);
  }
  return "";
}

/* Druh → tab. Staví se JEN z úvěrových tabů: druh pojistky tab neurčuje
 * (pojistky mají vlastní pole `pojistky`, nemíchají se s úvěry). */
export const DRUH_NA_TAB = UVEROVE_TABY.reduce((acc, tab) => {
  for (const d of DRUHY[tab]) acc[d.k] = tab;
  return acc;
}, {});

/** Výchozí druh pro nově založený řádek v tabu. */
export const VYCHOZI_DRUH = {
  [HYPOTEKY]: "hypoteka",
  [OSOBNI]: "spotrebitelsky",
  [FIREMNI]: "podnikatelsky",
  [POJISTNE]: "",
};

/**
 * Do kterého datového pole se ukládá nový řádek daného tabu.
 * Hypotéky do `zavazkyB`, osobní i firemní do `zavazkyO` — datový tvar
 * uložených analýz zůstává beze změny.
 */
export const ZDROJ_POLE = {
  [HYPOTEKY]: "zavazkyB",
  [OSOBNI]: "zavazkyO",
  [FIREMNI]: "zavazkyO",
};

/** Fallback pro řádky bez `druh` (= všechny existující analýzy). */
export const LEGACY_TAB = { zavazkyB: HYPOTEKY, zavazkyO: OSOBNI };

/* PŘEDNASTAVENÉ ŘÁDKY FORMULÁŘE — tady vznikl ten doménový nesmysl.
 * model.js nabízí v `zavazkyB` (dřív „bydlení") řádky „Spotřebitelské úvěry",
 * „Leasing" a „Kreditky", v `zavazkyO` (dřív „ostatní") „Podnikatelský úvěr".
 * Kdyby se migrace řídila jen polem, skončily by kreditky pod Hypotékami.
 *
 * Mapa se použije JEN tehdy, když má řádek pořád svoje původní id A původní
 * název — tzn. poradce ho nepřejmenoval na něco jiného. Přejmenovaný řádek
 * padá zpátky na pravidlo podle pole, protože o jeho obsahu už nic nevíme.
 * Ať tak, nebo tak, řádek NIKDY nezmizí — jen se liší tab. */
export const SABLONA_DRUH = {
  zb1: { nazev: "Hypoteční úvěr / úvěr na bydlení", druh: "hypoteka" },
  zb2: { nazev: "Spotřebitelské úvěry", druh: "spotrebitelsky" },
  zb3: { nazev: "Leasing", druh: "leasing_osobni" },
  zb4: { nazev: "Kreditky", druh: "kreditni_karta" },
  zo1: { nazev: "Podnikatelský úvěr", druh: "podnikatelsky" },
  zo2: { nazev: "Revolvingový úvěr", druh: "revolving" },
};

const stejnyNazev = (a, b) => String(a ?? "").trim().toLowerCase() === String(b ?? "").trim().toLowerCase();

/** Druh odvozený z nepřejmenovaného přednastaveného řádku, jinak null. */
export function druhZeSablony(row) {
  const s = SABLONA_DRUH[String(row?.id ?? "")];
  if (!s) return null;
  return stejnyNazev(row?.oznaceni, s.nazev) ? s.druh : null;
}

/**
 * Do kterého tabu řádek patří.
 * Pořadí rozhodování:
 *   1) `druh` zapsaný poradcem (může ho kdykoli přepnout a řádek se přesune),
 *   2) nepřejmenovaný přednastavený řádek → druh podle šablony,
 *   3) pole, ve kterém řádek leží.
 * NIKDY nevrací null — každý řádek musí někde skončit.
 */
export function tabZavazku(row, zdroj) {
  const d = String(row?.druh ?? "").trim();
  if (d && DRUH_NA_TAB[d]) return DRUH_NA_TAB[d];
  const ze = druhZeSablony(row);
  if (ze && DRUH_NA_TAB[ze]) return DRUH_NA_TAB[ze];
  return LEGACY_TAB[String(zdroj ?? "").trim()] || OSOBNI;
}

/**
 * Rozklad všech úvěrových řádků analýzy do tří tabů.
 * Vrací položky ve tvaru { r, zdroj } — `zdroj` je nutný pro zápis
 * (patch musí jít do stejného pole, odkud řádek přišel).
 *
 * TOTÁLNÍ ROZKLAD: součet délek = zavazkyB.length + zavazkyO.length.
 */
export function rozdelZavazky(d = {}) {
  const out = { [HYPOTEKY]: [], [OSOBNI]: [], [FIREMNI]: [] };
  const zdroje = [
    ["zavazkyB", Array.isArray(d?.zavazkyB) ? d.zavazkyB : []],
    ["zavazkyO", Array.isArray(d?.zavazkyO) ? d.zavazkyO : []],
  ];
  for (const [zdroj, rows] of zdroje) {
    for (const r of rows) {
      if (!r) continue;
      out[tabZavazku(r, zdroj)].push({ r, zdroj });
    }
  }
  return out;
}

/**
 * Data pro souhrnZavazkuTab() tak, aby pravý panel ukazoval JEN to, co je
 * v levém panelu vidět.
 *
 * PROČ: souhrnZavazkuTab(d, "ostatni") čte celé `d.zavazkyO`. Kdyby se mu
 * předala celá analýza, hlásil by na tabu Firemní úvěry i osobní kreditky —
 * přesně ta chyba, kvůli které vznikl souhrn podle tabu.
 */
export function dataProTab(d = {}, tab) {
  const t = String(tab ?? "").trim();
  if (!UVEROVE_TABY.includes(t)) return d;
  const b = rozdelZavazky(d);
  const rows = (b[t] || []).map((x) => x.r);
  return t === HYPOTEKY
    ? { ...d, zavazkyB: rows, zavazkyO: [] }
    : { ...d, zavazkyB: [], zavazkyO: rows };
}

/**
 * Pole „Druh" do karty závazku.
 * Nabízí VŠECHNY úvěrové druhy napříč taby — právě tím jde řádek přesunout
 * z Osobních do Firemních, aniž by se s daty muselo hýbat. Ukládá se KLÍČ
 * (hypoteka, spotrebitelsky…), ne český popisek: podle klíče se rozhoduje tab.
 */
export function poleDruhUver(lang = "cs") {
  return {
    k: "druh",
    l: POPIS_DRUH_UVERU[lang] || POPIS_DRUH_UVERU.cs,
    type: "select",
    wide: true,
    moznosti: UVEROVE_TABY.flatMap((t) => DRUHY[t].map((x) => ({ v: x.k, l: druhPopisek(x, lang) }))),
  };
}

/** Pole „Druh smlouvy" do karty pojistky (druh tab neurčuje, jen popisuje). */
export function poleDruhPojistky(lang = "cs") {
  return {
    k: "druh",
    l: POPIS_DRUH_POJISTENI[lang] || POPIS_DRUH_POJISTENI.cs,
    type: "select",
    wide: true,
    moznosti: DRUHY[POJISTNE].map((x) => ({ v: x.k, l: druhPopisek(x, lang) })),
  };
}

/* Výběr „Koho pojišťuje“ (pole `osoba` v bublině, 24. 9. 2026) odešel
 * 14. 10. 2026 — kdo je pojištěný, říkají rizika po osobách
 * (lib/analyzaV2/kohoPojistuje.js, components/analyza/RizikaSmluv.js).
 * `osoba` se dál ukládá, ale jen odvozeně z rizik. */

/* „RUŠÍ SE / ZŮSTÁVÁ“ (rozhodnutí majitele 30. 9. 2026 bod 2): když nová
 * životní smlouva nahradí tuhle, plán odečte jen ROZDÍL pojistného. Prázdno =
 * výchozí pravidlo `lib/srovnaniPojisteni.rusiSeVychozi` (rizikové životní,
 * úrazové a smlouva s limitem smrti / invalidity se ruší; majetek, odpovědnost,
 * povinné ručení a právní ochrana nikdy). Ukládá se "ano" / "ne" — tytéž
 * hodnoty zapisuje přepínač v Návrhu řešení. */
const POPIS_RUSI = {
  cs: "S novou smlouvou", sk: "S novou zmluvou", en: "With the new policy",
  uk: "З новим договором", vi: "Khi có hợp đồng mới",
};
const RUSI_VOLBY = [
  { k: "ano", cs: "ruší se", sk: "ruší sa", en: "cancelled", uk: "скасовується", vi: "hủy" },
  { k: "ne", cs: "zůstává", sk: "zostáva", en: "kept", uk: "залишається", vi: "giữ lại" },
];

/** Pole „ruší se / zůstává“ do bubliny pojistky (prázdno = výchozí pravidlo). */
export function poleRusiSe(lang = "cs") {
  return {
    k: "rusi",
    l: POPIS_RUSI[lang] || POPIS_RUSI.cs,
    type: "select",
    moznosti: RUSI_VOLBY.map((x) => ({ v: x.k, l: druhPopisek(x, lang) })),
  };
}

/** Řádky (holé) pro tab — pro pruhy v pravém panelu. */
export function radkyProTab(d = {}, tab) {
  const t = String(tab ?? "").trim();
  if (UVEROVE_TABY.includes(t)) return (rozdelZavazky(d)[t] || []).map((x) => x.r);
  if (t === POJISTNE) return Array.isArray(d?.pojistky) ? d.pojistky : [];
  return [];
}
